// Go / no-go decisions on a Pursuit (SAD-488 / CAP-04).
//
// A decision is an append-only record: Go, No-Go or Defer, for the whole
// Pursuit or one workstream of its capture tasks, recorded whenever the team
// decides, optionally closing a scheduled gate. The evidence the user sees before saving
// is stored with the record. "Draft with AI" only fills the form; nothing is
// saved until the user clicks Record. Recording never changes the Pursuit
// stage or its tasks. Decisions made before scoped records existed are shown
// read-only below the history.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraBadge, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraEmptyState, SpecraFormField, SpecraInlineBanner, SpecraInput, SpecraOverlayDrawer, SpecraSelect, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useState } from 'react';
import { useDiscardGoNoGoDraft, useDraftGoNoGo, useGoNoGoEvidence, useGoNoGoRecords, useRecordGoNoGo } from './goNoGoHooks';
import { ReadOnlyScope } from './record/ReadOnlyScope';
import { useCaptureReviews } from './reviewHooks';
const OUTCOME_LABEL = {
    go: 'Go',
    no_go: 'No-Go',
    defer: 'Defer',
    pending: 'Pending'
};
const OUTCOME_VARIANT = {
    go: 'success',
    no_go: 'danger',
    defer: 'info',
    pending: 'warning'
};
const CHOICES = [
    {
        outcome: 'go',
        testId: Testids.capture.goNoGoChoiceGo
    },
    {
        outcome: 'no_go',
        testId: Testids.capture.goNoGoChoiceNoGo
    },
    {
        outcome: 'defer',
        testId: Testids.capture.goNoGoChoiceDefer
    }
];
function isDenied(err) {
    return err instanceof ConnectError && err.code === Code.PermissionDenied;
}
function errorText(err) {
    return err instanceof Error ? err.message : 'Something went wrong';
}
function whenText(ts) {
    return ts ? ts.toDate().toLocaleString() : '';
}
// specra-allow local-composite: Maps a go/no-go outcome to the shared SpecraBadge tone.
function OutcomeBadge({ outcome, testId }) {
    return /*#__PURE__*/ _jsx(SpecraBadge, {
        variant: OUTCOME_VARIANT[outcome] ?? 'warning',
        "data-testid": testId,
        children: OUTCOME_LABEL[outcome] ?? outcome
    });
}
function EvidenceSummary({ snapshot }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2 text-sm",
        children: [
            /*#__PURE__*/ _jsxs("dl", {
                className: "grid grid-cols-2 gap-x-4 gap-y-1",
                children: [
                    /*#__PURE__*/ _jsx("dt", {
                        className: "text-muted-foreground",
                        children: "Pursuit stage"
                    }),
                    /*#__PURE__*/ _jsx("dd", {
                        children: snapshot.pursuitStage || '—'
                    }),
                    /*#__PURE__*/ _jsx("dt", {
                        className: "text-muted-foreground",
                        children: "Bid position"
                    }),
                    /*#__PURE__*/ _jsx("dd", {
                        children: snapshot.bidPosition || '—'
                    }),
                    /*#__PURE__*/ _jsx("dt", {
                        className: "text-muted-foreground",
                        children: "Win probability"
                    }),
                    /*#__PURE__*/ _jsx("dd", {
                        children: snapshot.pwinPercent ? `${snapshot.pwinPercent}%` : '—'
                    }),
                    /*#__PURE__*/ _jsx("dt", {
                        className: "text-muted-foreground",
                        children: "Unresolved tasks"
                    }),
                    /*#__PURE__*/ _jsxs("dd", {
                        children: [
                            snapshot.unresolvedTaskCount,
                            " of ",
                            snapshot.tasks.length
                        ]
                    }),
                    /*#__PURE__*/ _jsx("dt", {
                        className: "text-muted-foreground",
                        children: "Customer interactions"
                    }),
                    /*#__PURE__*/ _jsx("dd", {
                        children: snapshot.interactions.length
                    }),
                    /*#__PURE__*/ _jsx("dt", {
                        className: "text-muted-foreground",
                        children: "Earlier decisions"
                    }),
                    /*#__PURE__*/ _jsx("dd", {
                        children: snapshot.priorRecords.length + snapshot.legacyDecisions.length
                    })
                ]
            }),
            snapshot.profileRisks && /*#__PURE__*/ _jsxs("p", {
                className: "text-muted-foreground",
                children: [
                    "Risks: ",
                    snapshot.profileRisks
                ]
            }),
            snapshot.tasks.length > 0 && /*#__PURE__*/ _jsx("ul", {
                className: "list-disc pl-5 text-muted-foreground",
                children: snapshot.tasks.map((t)=>/*#__PURE__*/ _jsxs("li", {
                        children: [
                            t.title,
                            " (",
                            t.status,
                            ")"
                        ]
                    }, t.id))
            })
        ]
    });
}
function HistoryRow({ record }) {
    const snapshot = record.evidenceSnapshot;
    return /*#__PURE__*/ _jsxs("li", {
        className: "space-y-1 rounded-md border p-3",
        "data-testid": Testids.capture.goNoGoHistoryRow,
        "data-record-id": record.id,
        "data-outcome": record.outcome,
        "data-scope-kind": record.scopeKind,
        "data-current": record.isCurrent ? 'true' : 'false',
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2 text-sm",
                children: [
                    /*#__PURE__*/ _jsx(OutcomeBadge, {
                        outcome: record.outcome
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        children: record.scopeKind === 'workstream' ? `Workstream: ${record.scopeLabel || 'Unnamed'} (${record.scopeTaskIds.length} tasks)` : 'Whole Pursuit'
                    }),
                    record.isCurrent && /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "neutral",
                        children: "Current"
                    }),
                    record.aiDraftId && /*#__PURE__*/ _jsx("span", {
                        className: "text-xs text-muted-foreground",
                        children: "Started from an AI draft"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "ml-auto text-xs text-muted-foreground",
                        children: whenText(record.decidedAt)
                    })
                ]
            }),
            record.rationale && /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: record.rationale
            }),
            snapshot && /*#__PURE__*/ _jsxs("details", {
                className: "text-sm",
                children: [
                    /*#__PURE__*/ _jsxs("summary", {
                        className: "cursor-pointer text-xs font-medium",
                        children: [
                            "Evidence when this was decided (",
                            whenText(record.evidenceCapturedAt),
                            ")"
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "pt-2",
                        children: /*#__PURE__*/ _jsx(EvidenceSummary, {
                            snapshot: snapshot
                        })
                    })
                ]
            })
        ]
    });
}
export function GoNoGoPanel({ captureProjectId, decisions, tasks = [], correlationId, readOnly = false, gateToRecord, open: openProp, onOpenChange }) {
    const reviews = useCaptureReviews(captureProjectId);
    const dueGates = (reviews.data ?? []).filter((review)=>review.kind === 'gate' && review.status === 'open' && !review.goNoGoRecordId);
    const [ownOpen, setOwnOpen] = useState(false);
    const controlled = onOpenChange !== undefined;
    const open = controlled ? !!openProp : ownOpen;
    const setOpen = onOpenChange ?? setOwnOpen;
    const [gateReviewId, setGateReviewId] = useState('');
    const [gateLabel, setGateLabel] = useState('');
    const [gateLinkWarning, setGateLinkWarning] = useState('');
    useEffect(()=>{
        if (!gateToRecord || readOnly) return;
        setGateReviewId(gateToRecord.id);
        setOpen(true);
    }, [
        gateToRecord,
        readOnly,
        setOpen
    ]);
    const evidence = useGoNoGoEvidence(captureProjectId);
    const records = useGoNoGoRecords(captureProjectId);
    const record = useRecordGoNoGo(correlationId);
    const draftMutation = useDraftGoNoGo();
    const discard = useDiscardGoNoGoDraft();
    const [outcome, setOutcome] = useState('');
    const [scopeKind, setScopeKind] = useState('pursuit');
    const [scopeLabel, setScopeLabel] = useState('');
    const [taskIds, setTaskIds] = useState([]);
    const [rationale, setRationale] = useState('');
    const [draft, setDraft] = useState(null);
    const [recordError, setRecordError] = useState(null);
    const [recordDenied, setRecordDenied] = useState(false);
    const list = records.data ?? [];
    const denied = recordDenied || isDenied(records.error) || isDenied(evidence.error);
    const loadError = !denied && (records.error || evidence.error) ? errorText(records.error ?? evidence.error) : '';
    const currentRecords = list.filter((r)=>r.isCurrent);
    const busy = record.isPending || draftMutation.isPending || discard.isPending;
    const workstreamValid = scopeKind === 'pursuit' || scopeLabel.trim() !== '' && taskIds.length > 0;
    const canRecord = outcome !== '' && workstreamValid && !busy;
    const resetForm = ()=>{
        setGateReviewId('');
        setGateLabel('');
        setOutcome('');
        setScopeKind('pursuit');
        setScopeLabel('');
        setTaskIds([]);
        setRationale('');
        setDraft(null);
    };
    const onRecord = ()=>{
        if (!outcome || !canRecord) return;
        setRecordError(null);
        setGateLinkWarning('');
        record.mutate({
            captureProjectId,
            outcome,
            scopeKind,
            scopeLabel: scopeKind === 'workstream' ? scopeLabel.trim() : '',
            scopeTaskIds: scopeKind === 'workstream' ? taskIds : [],
            rationale: rationale.trim(),
            aiDraftId: draft?.id,
            gateReviewId,
            gateLabel: gateReviewId ? '' : gateLabel.trim()
        }, {
            onSuccess: (response)=>{
                resetForm();
                setOpen(false);
                setGateLinkWarning(response.gateLinkWarning ?? '');
            },
            onError: (err)=>{
                if (isDenied(err)) setRecordDenied(true);
                else setRecordError(errorText(err));
            }
        });
    };
    const onDraft = ()=>{
        setRecordError(null);
        draftMutation.mutate({
            captureProjectId,
            scopeKind,
            scopeLabel: scopeKind === 'workstream' ? scopeLabel.trim() : '',
            scopeTaskIds: scopeKind === 'workstream' ? taskIds : []
        }, {
            onSuccess: (d)=>{
                if (!d) return;
                setDraft(d);
                setOutcome(d.suggestedOutcome);
                setScopeKind(d.suggestedScopeKind === 'workstream' ? 'workstream' : 'pursuit');
                setScopeLabel(d.suggestedScopeLabel);
                setTaskIds(d.suggestedScopeTaskIds);
                setRationale(d.suggestedRationale);
            },
            onError: (err)=>{
                if (isDenied(err)) setRecordDenied(true);
            }
        });
    };
    const onDismiss = ()=>{
        const id = draft?.id;
        resetForm();
        if (id) discard.mutate({
            captureProjectId,
            draftId: id
        });
    };
    const toggleTask = (id)=>setTaskIds((prev)=>prev.includes(id) ? prev.filter((x)=>x !== id) : [
                ...prev,
                id
            ]);
    const canAdd = !readOnly && !denied;
    const isEmpty = list.length === 0 && decisions.length === 0;
    const pursuitCurrent = currentRecords.find((r)=>r.scopeKind === 'pursuit');
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(ReadOnlyScope, {
                readOnly: readOnly,
                children: /*#__PURE__*/ _jsxs(SpecraCard, {
                    "data-testid": Testids.capture.goNoGoPanel,
                    children: [
                        /*#__PURE__*/ _jsxs(SpecraCardHeader, {
                            className: "flex flex-row items-center justify-between space-y-0 py-4",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraCardTitle, {
                                    children: "Go / no-go"
                                }),
                                pursuitCurrent && /*#__PURE__*/ _jsx(OutcomeBadge, {
                                    outcome: pursuitCurrent.outcome,
                                    testId: Testids.capture.goNoGoOutcome
                                }),
                                canAdd && !controlled && !isEmpty && /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    onClick: ()=>setOpen(true),
                                    "data-testid": Testids.pursuitDecisions.addButton,
                                    children: "Add decision"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(SpecraCardContent, {
                            className: "space-y-4",
                            children: [
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-muted-foreground",
                                    children: "Record Go, No-Go or Defer whenever the decision is made. Choose the whole Pursuit or just one workstream. Earlier decisions are kept; a new one never rewrites them or changes the Pursuit's stage or tasks."
                                }),
                                denied && /*#__PURE__*/ _jsx("p", {
                                    className: "rounded-md border border-warning/25 bg-warning-muted p-3 text-sm text-warning",
                                    "data-testid": Testids.capture.goNoGoDenied,
                                    children: "You don't have permission to record go/no-go decisions on this Pursuit. Ask a Growth owner to record one or to grant you access."
                                }),
                                loadError && /*#__PURE__*/ _jsxs("p", {
                                    className: "text-sm text-danger",
                                    role: "alert",
                                    "data-testid": Testids.capture.goNoGoError,
                                    children: [
                                        "Couldn't load go/no-go decisions: ",
                                        loadError
                                    ]
                                }),
                                gateLinkWarning && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                                    tone: "warning",
                                    children: gateLinkWarning
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-2",
                                    children: [
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-xs font-medium",
                                            children: "Decision history"
                                        }),
                                        records.isLoading ? /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm text-muted-foreground",
                                            children: "Loading decisions…"
                                        }) : list.length === 0 && decisions.length === 0 ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                            size: "compact",
                                            title: "No decisions yet.",
                                            description: "Record one whenever you're ready.",
                                            "data-testid": Testids.pursuitDecisions.emptyState,
                                            action: canAdd ? /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "sm",
                                                onClick: ()=>setOpen(true),
                                                "data-testid": Testids.pursuitDecisions.addButton,
                                                children: "Add decision"
                                            }) : undefined
                                        }) : /*#__PURE__*/ _jsx("ul", {
                                            className: "space-y-2",
                                            children: list.map((r)=>/*#__PURE__*/ _jsx(HistoryRow, {
                                                    record: r
                                                }, r.id))
                                        })
                                    ]
                                }),
                                decisions.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-1",
                                    children: [
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-xs font-medium",
                                            children: "Earlier decisions (before scoped records)"
                                        }),
                                        /*#__PURE__*/ _jsx("ul", {
                                            className: "space-y-1",
                                            children: decisions.map((d)=>/*#__PURE__*/ _jsxs("li", {
                                                    className: "flex items-center gap-2 text-sm",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(OutcomeBadge, {
                                                            outcome: d.outcome
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "truncate text-muted-foreground",
                                                            children: d.notes
                                                        })
                                                    ]
                                                }, d.id))
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                })
            }),
            canAdd && /*#__PURE__*/ _jsx(SpecraOverlayDrawer, {
                open: open,
                onOpenChange: setOpen,
                title: "Record go / no-go decision",
                testId: Testids.pursuitDecisions.dialog,
                width: "lg",
                footerProps: {
                    leading: /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        size: "sm",
                        onClick: onDraft,
                        disabled: busy || !workstreamValid,
                        "data-testid": Testids.capture.goNoGoDraftAi,
                        children: draftMutation.isPending ? 'Drafting…' : 'Draft with AI'
                    }),
                    onCancel: ()=>setOpen(false),
                    confirmLabel: record.isPending ? 'Recording…' : 'Record decision',
                    onConfirm: onRecord,
                    confirmProps: {
                        disabled: !canRecord,
                        'data-testid': Testids.capture.goNoGoRecord
                    }
                },
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            "data-testid": Testids.capture.goNoGoEvidence,
                            children: [
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-xs font-medium",
                                    children: "Evidence right now (saved with your decision)"
                                }),
                                evidence.isLoading ? /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-muted-foreground",
                                    children: "Loading evidence…"
                                }) : evidence.data ? /*#__PURE__*/ _jsx(EvidenceSummary, {
                                    snapshot: evidence.data
                                }) : /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-muted-foreground",
                                    children: "No evidence available yet."
                                })
                            ]
                        }),
                        draft && /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-2 rounded-md border border-violet/20 bg-violet-muted p-3 text-sm",
                            "data-testid": Testids.capture.goNoGoDraftBanner,
                            children: [
                                /*#__PURE__*/ _jsx("p", {
                                    className: "font-medium",
                                    children: "AI draft — review before saving"
                                }),
                                draft.whyLines.length > 0 && /*#__PURE__*/ _jsx("ul", {
                                    className: "list-disc pl-5",
                                    children: draft.whyLines.map((line)=>/*#__PURE__*/ _jsx("li", {
                                            children: line
                                        }, line))
                                }),
                                draft.ifAdopted && /*#__PURE__*/ _jsxs("p", {
                                    className: "text-muted-foreground",
                                    children: [
                                        "If you record this: ",
                                        draft.ifAdopted
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-xs text-muted-foreground",
                                    children: "Nothing is saved until you click Record decision. Edit anything below first."
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    variant: "ghost",
                                    size: "sm",
                                    onClick: onDismiss,
                                    disabled: busy,
                                    "data-testid": Testids.capture.goNoGoDraftDismiss,
                                    children: "Dismiss"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-3",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "For gate",
                                    htmlFor: "go-no-go-gate",
                                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                        id: "go-no-go-gate",
                                        value: gateReviewId,
                                        onChange: (event)=>setGateReviewId(event.target.value),
                                        disabled: busy || reviews.isLoading || !!reviews.error,
                                        "data-testid": Testids.capture.goNoGoGateSelect,
                                        children: [
                                            /*#__PURE__*/ _jsx("option", {
                                                value: "",
                                                children: "No gate"
                                            }),
                                            dueGates.map((gate)=>/*#__PURE__*/ _jsx("option", {
                                                    value: gate.id,
                                                    children: gate.title
                                                }, gate.id))
                                        ]
                                    })
                                }),
                                !gateReviewId && /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "Gate label",
                                    htmlFor: "go-no-go-gate-label",
                                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                                        id: "go-no-go-gate-label",
                                        value: gateLabel,
                                        onChange: (event)=>setGateLabel(event.target.value),
                                        disabled: busy,
                                        "data-testid": Testids.capture.goNoGoGateInput
                                    })
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-xs font-medium",
                                    children: "Decision"
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "flex flex-wrap gap-2",
                                    children: CHOICES.map((c)=>/*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "sm",
                                            variant: outcome === c.outcome ? 'default' : 'secondary',
                                            "aria-pressed": outcome === c.outcome,
                                            onClick: ()=>setOutcome(c.outcome),
                                            disabled: busy,
                                            "data-testid": c.testId,
                                            children: OUTCOME_LABEL[c.outcome]
                                        }, c.outcome))
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-xs font-medium",
                                    children: "Applies to"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-wrap gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "sm",
                                            variant: scopeKind === 'pursuit' ? 'default' : 'secondary',
                                            "aria-pressed": scopeKind === 'pursuit',
                                            onClick: ()=>setScopeKind('pursuit'),
                                            disabled: busy,
                                            "data-testid": Testids.capture.goNoGoScopePursuit,
                                            children: "Whole Pursuit"
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "sm",
                                            variant: scopeKind === 'workstream' ? 'default' : 'secondary',
                                            "aria-pressed": scopeKind === 'workstream',
                                            onClick: ()=>setScopeKind('workstream'),
                                            disabled: busy,
                                            "data-testid": Testids.capture.goNoGoScopeWorkstream,
                                            children: "One workstream"
                                        })
                                    ]
                                }),
                                scopeKind === 'workstream' && /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-2 rounded-md border p-3",
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraFormField, {
                                            label: "Workstream name",
                                            htmlFor: "go-no-go-scope-label",
                                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                                id: "go-no-go-scope-label",
                                                value: scopeLabel,
                                                onChange: (e)=>setScopeLabel(e.target.value),
                                                placeholder: "e.g. Teaming with Acme",
                                                disabled: busy,
                                                "data-testid": Testids.capture.goNoGoScopeLabel
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-xs font-medium",
                                            children: "Tasks in this workstream"
                                        }),
                                        tasks.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm text-muted-foreground",
                                            children: "Add capture tasks to this Pursuit first, then pick the ones this decision covers."
                                        }) : /*#__PURE__*/ _jsx("ul", {
                                            className: "space-y-1",
                                            children: tasks.map((t)=>/*#__PURE__*/ _jsx("li", {
                                                    children: /*#__PURE__*/ _jsxs("label", {
                                                        className: "flex items-center gap-2 text-sm",
                                                        children: [
                                                            /*#__PURE__*/ _jsx("input", {
                                                                type: "checkbox",
                                                                checked: taskIds.includes(t.id),
                                                                onChange: ()=>toggleTask(t.id),
                                                                disabled: busy,
                                                                "data-testid": Testids.capture.goNoGoScopeTask,
                                                                "data-task-id": t.id
                                                            }),
                                                            t.title
                                                        ]
                                                    })
                                                }, t.id))
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "Why",
                                    htmlFor: "go-no-go-rationale",
                                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                        id: "go-no-go-rationale",
                                        value: rationale,
                                        onChange: (e)=>setRationale(e.target.value),
                                        rows: 3,
                                        placeholder: "The reasoning behind this decision.",
                                        disabled: busy,
                                        "data-testid": Testids.capture.goNoGoRationale
                                    })
                                })
                            ]
                        }),
                        recordError && /*#__PURE__*/ _jsxs("p", {
                            className: "text-sm text-danger",
                            role: "alert",
                            "data-testid": Testids.capture.goNoGoError,
                            children: [
                                "Couldn't record the decision: ",
                                recordError
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
